import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { contact } from "@/lib/healic-data";
import {
  Phone,
  Mail,
  MessageCircle,
  Users,
  Stethoscope,
  Gauge,
  Star,
  Building2,
  Heart,
  ShieldCheck,
  HandHeart,
  Target,
  Eye,
  Linkedin,
  Quote,
} from "lucide-react";
import doctorsTeam from "@/assets/about-doctors-team.jpg";
import ceoPortrait from "@/assets/ceo-manish-gupta.png";

const SITE_URL = "https://healic.in";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Healic — Trusted Planned Surgery Hospital Chain" },
      {
        name: "description",
        content:
          "Healic is building India's most trusted planned surgery hospital chain — trustworthy, top-quality, patient-centric healthcare at affordable rates.",
      },
      { property: "og:title", content: "About Healic Multispeciality Hospital" },
      {
        property: "og:description",
        content:
          "Our story, vision, mission and values. Meet CEO Manish Gupta and learn why 50,000+ patients trust Healic each year.",
      },
      { property: "og:url", content: `${SITE_URL}/about` },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${SITE_URL}/og-default.png` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/about` }],
  }),
  component: AboutPage,
});

const values = [
  {
    icon: Heart,
    title: "Patient First",
    body: "Every decision starts with what's right for the patient and family.",
  },
  {
    icon: ShieldCheck,
    title: "Trust & Transparency",
    body: "Fixed prices, clear conversations and no hidden surprises — ever.",
  },
  {
    icon: HandHeart,
    title: "Parivar Jaisi Dekhbhal",
    body: "Family-like care through every step — admission to recovery.",
  },
  {
    icon: Stethoscope,
    title: "Clinical Excellence",
    body: "Senior surgeons, modern OTs and evidence-based protocols.",
  },
];

const trustStats = [
  { icon: Users, value: "50,000+", label: "Patients in Last 1 Year" },
  { icon: Stethoscope, value: "100+", label: "Specialist Doctors & Surgeons" },
  { icon: Gauge, value: "85%+", label: "NPS Score" },
  { icon: Star, value: "Average 4.8", label: "Google Rating" },
  { icon: Building2, value: "10 Hospitals", label: "by Mar '26" },
];

const testimonials = [
  {
    quote:
      "From the first call to discharge, the Healic team treated us like family. Fixed price, no surprises, and my surgery went smoothly.",
    name: "Rahul S.",
    city: "Gurugram",
    treatment: "Piles surgery",
  },
  {
    quote:
      "The doctors explained everything in simple language. My mother's knee replacement recovery was much faster than we expected.",
    name: "Anjali M.",
    city: "Ghaziabad",
    treatment: "Knee replacement",
  },
  {
    quote:
      "Affordable, clean and genuinely caring. Healic is what every Indian hospital should feel like.",
    name: "Imran K.",
    city: "Agra",
    treatment: "Gallbladder removal",
  },
];

function AboutPage() {
  return (
    <PageShell>
      {/* 1. Banner */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0">
          <img
            src={doctorsTeam}
            alt="Healic team of doctors and surgeons"
            fetchPriority="high"
            decoding="async"
            width={1600}
            height={1024}
            className="h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/85 to-primary/50" />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-12 lg:grid-cols-[1.2fr_1fr] lg:items-center lg:px-8 lg:py-16">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">
              About Healic
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
              Building India's Most Trusted Planned Surgery Hospital Chain
            </h1>
            <p className="mt-4 max-w-2xl text-sm opacity-85 sm:text-base">
              At Healic, we are focussed on providing trustworthy, top-quality,
              patient-centric healthcare services at affordable rates.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="rounded-pill bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground shadow-cta hover:opacity-90"
              >
                Request Call Back
              </Link>
              <a
                href={`tel:${contact.phoneTel}`}
                className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-5 py-2.5 text-sm font-semibold ring-1 ring-white/20 hover:bg-white/15"
              >
                <Phone className="h-4 w-4 text-accent" /> {contact.phone}
              </a>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative overflow-hidden rounded-3xl ring-1 ring-white/20 shadow-card">
              <img
                src={doctorsTeam}
                alt="Healic doctors"
                loading="lazy"
                decoding="async"
                width={1600}
                height={1024}
                className="h-[280px] w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 2. Vision / Mission / Values */}
      <section className="mx-auto max-w-7xl px-4 py-20 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-border bg-card p-8 shadow-soft">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent">
              <Eye className="h-6 w-6" />
            </div>
            <h2 className="mt-5 font-display text-2xl font-bold">Our Vision</h2>
            <p className="mt-3 text-muted-foreground">
              To be India's most trusted and loved planned surgery hospital
              chain — making world-class surgical care accessible, affordable
              and warm for every Indian family.
            </p>
          </div>
          <div className="rounded-3xl border border-border bg-card p-8 shadow-soft">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent">
              <Target className="h-6 w-6" />
            </div>
            <h2 className="mt-5 font-display text-2xl font-bold">Our Mission</h2>
            <p className="mt-3 text-muted-foreground">
              To deliver trustworthy, top-quality, patient-centric surgical
              care at fair, transparent prices — backed by experienced
              surgeons, modern infrastructure and parivar-jaisi dekhbhal.
            </p>
          </div>
        </div>

      </section>

      {/* 3. Meet Our CEO */}
      <section className="bg-accent-soft/40">
        <div className="mx-auto max-w-7xl px-4 py-20 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.4fr] lg:items-start">
            <div className="relative">
              <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-card">
                <img
                  src={ceoPortrait}
                  alt="Manish Gupta — CEO, Healic"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="rounded-3xl bg-card shadow-soft ring-1 ring-border">
              <div className="flex items-start justify-between gap-4 rounded-t-3xl bg-primary px-7 py-5 text-primary-foreground">
                <div>
                  <h2 className="font-display text-2xl font-bold">Manish Gupta</h2>
                  <p className="text-sm opacity-80">CEO</p>
                </div>
                <a
                  href="https://www.linkedin.com/company/healic-health/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                  className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-primary hover:opacity-90"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
              <div className="space-y-4 p-7 text-sm leading-relaxed text-foreground/85">
                <p>
                  Manish is a serial entrepreneur, with Healic being his latest
                  venture in a series of successful startups that he has
                  co-founded, scaled, and exited in e-Learning, cloud telephony,
                  and F&amp;B sectors.
                </p>
                <p>
                  With over 20 years of experience in business development,
                  entrepreneurship, and management, Manish has developed
                  expertise in identifying market opportunities, building
                  teams, overcoming challenges, and creating value. Manish has
                  received multiple honors during this journey — most notable
                  being <strong>40 under 40 Entrepreneurs in India by Fortune
                  Magazine</strong>.
                </p>
                <p>
                  Besides his professional pursuits, Manish enjoys karate (he
                  is currently Black Belt, First Dan), running (has run quite
                  a few half marathons and a full marathon on the Great Wall
                  of China — one of the toughest, they say), and reading.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Why Trust Us */}
      <section className="mx-auto max-w-7xl px-4 py-20 lg:px-8">
        <div className="text-center">
          <h2 className="font-display text-3xl font-bold sm:text-4xl">
            Why Patients Trust Healic?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            Numbers that reflect our commitment to outcomes, experience and
            accessibility.
          </p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {trustStats.map((s) => (
            <div
              key={s.label}
              className="flex flex-col items-center rounded-3xl border border-border bg-card p-6 text-center shadow-soft"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                <s.icon className="h-7 w-7 text-accent" />
              </div>
              <p className="mt-4 font-display text-2xl font-bold">{s.value}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. Testimonials */}
      <section className="bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-20 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">
              Patient Stories
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">
              What Our Patients say
            </h2>
            <p className="mt-3 text-muted-foreground">
              Real words from families who chose Healic for planned surgery.
            </p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure
                key={t.name}
                className="flex h-full flex-col rounded-3xl border border-border bg-card p-7 shadow-soft"
              >
                <Quote className="h-7 w-7 text-accent" />
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground/85">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 border-t border-border pt-4">
                  <p className="font-display text-base font-semibold">{t.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t.treatment} • {t.city}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Contact & Support */}
      <section className="mx-auto max-w-7xl px-4 py-20 lg:px-8">
        <div className="rounded-3xl bg-accent-soft/50 p-8 lg:p-12">
          <h2 className="font-display text-3xl font-bold sm:text-4xl">
            Contact &amp; Support
          </h2>
          <p className="mt-2 text-muted-foreground">
            Quick help options — get answers fast.
          </p>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            <ContactCard
              icon={Phone}
              title="7 Am to 12 Am Helpline"
              body={contact.phone}
              sub="Speak to our care team anytime"
              href={`tel:${contact.phoneTel}`}
              cta="Call Now"
              ctaClass="bg-primary text-primary-foreground"
            />
            <ContactCard
              icon={Mail}
              title="Email Support"
              body={contact.email}
              sub="Share reports or documents easily"
              href={`mailto:${contact.email}`}
              cta="Email Now"
              ctaClass="bg-accent text-accent-foreground"
            />
            <ContactCard
              icon={MessageCircle}
              title="WhatsApp Chat"
              body="Instant help. Quick replies."
              sub="Share images & get confirmation fast"
              href={`https://wa.me/${contact.whatsapp}`}
              cta="Chat on WhatsApp"
              ctaClass="bg-emerald-500 text-white"
            />
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function ContactCard({
  icon: Icon,
  title,
  body,
  sub,
  href,
  cta,
  ctaClass,
}: {
  icon: typeof Phone;
  title: string;
  body: string;
  sub: string;
  href: string;
  cta: string;
  ctaClass: string;
}) {
  return (
    <div className="flex flex-col rounded-2xl bg-card p-6 text-center shadow-soft">
      <h3 className="font-display text-lg font-bold">{title}</h3>
      <div className="mt-5 flex items-start gap-3 text-left">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-semibold">{body}</p>
          <p className="text-xs text-muted-foreground">{sub}</p>
        </div>
      </div>
      <a
        href={href}
        className={`mt-6 inline-block rounded-pill px-6 py-2.5 text-sm font-semibold shadow-cta hover:opacity-90 ${ctaClass}`}
      >
        {cta}
      </a>
    </div>
  );
}
